// Board decoration: rank/file labels on the frame and the brass corner studs.
import {
  BOARD_INLAY_OFFSET,
  BOARD_OUTER_OFFSET,
  BOARD_OUTER_SIZE,
  SQUARE_SIZE,
  WOOD_FRAME_THICKNESS,
} from '../utils/constants.js';
import { BOARD_SIZE } from '../game/squares.js';
import type { ViewerRotation } from './geometry.js';
import type { RenderPalette } from './palette.js';

// Capitals, as on the checkers board.
const COLUMN_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'] as const;

export function drawCoordinateLabels(
  ctx: CanvasRenderingContext2D,
  palette: RenderPalette,
  rotation: ViewerRotation,
): void {
  ctx.save();
  ctx.fillStyle = palette.labelColor;
  ctx.font = '600 12px "Segoe UI", system-ui, sans-serif';
  ctx.textBaseline = 'middle';

  const labelGap = WOOD_FRAME_THICKNESS / 2;

  // File labels (A–H) along top and bottom of the wood frame.
  ctx.textAlign = 'center';
  for (let i = 0; i < BOARD_SIZE; i += 1) {
    const col = rotation === 0 ? i : BOARD_SIZE - 1 - i;
    const label = COLUMN_LABELS[col] ?? '';
    const centerX = BOARD_INLAY_OFFSET + i * SQUARE_SIZE + SQUARE_SIZE / 2;
    ctx.fillText(label, centerX, BOARD_OUTER_OFFSET + labelGap);
    ctx.fillText(label, centerX, BOARD_OUTER_OFFSET + BOARD_OUTER_SIZE - labelGap);
  }

  // Rank labels (8–1 top→bottom from White's side) along left and right.
  ctx.textAlign = 'center';
  for (let i = 0; i < BOARD_SIZE; i += 1) {
    const row = rotation === 0 ? i : BOARD_SIZE - 1 - i;
    const rank = BOARD_SIZE - row;
    const centerY = BOARD_INLAY_OFFSET + i * SQUARE_SIZE + SQUARE_SIZE / 2;
    ctx.fillText(String(rank), BOARD_OUTER_OFFSET + labelGap, centerY);
    ctx.fillText(
      String(rank),
      BOARD_OUTER_OFFSET + BOARD_OUTER_SIZE - labelGap,
      centerY,
    );
  }

  ctx.restore();
}

export function drawBrassDiamond(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
): void {
  const r = 5;
  ctx.save();

  ctx.beginPath();
  ctx.moveTo(cx, cy - r - 1);
  ctx.lineTo(cx + r + 1, cy);
  ctx.lineTo(cx, cy + r + 1);
  ctx.lineTo(cx - r - 1, cy);
  ctx.closePath();
  ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
  ctx.fill();

  const grad = ctx.createLinearGradient(cx - r, cy - r, cx + r, cy + r);
  grad.addColorStop(0, '#ffe8a4');
  grad.addColorStop(0.5, '#d4a849');
  grad.addColorStop(1, '#7a5a1f');
  ctx.beginPath();
  ctx.moveTo(cx, cy - r);
  ctx.lineTo(cx + r, cy);
  ctx.lineTo(cx, cy + r);
  ctx.lineTo(cx - r, cy);
  ctx.closePath();
  ctx.fillStyle = grad;
  ctx.fill();

  ctx.strokeStyle = 'rgba(0, 0, 0, 0.35)';
  ctx.lineWidth = 0.5;
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(cx, cy, 0.8, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(60, 35, 10, 0.9)';
  ctx.fill();

  ctx.restore();
}
